import Header from "@/components/header";
import Footer from "@/components/footer";
import dynamic from "next/dynamic";

// Import CallToAction dynamically with SSR disabled to avoid framer-motion issues
const CallToAction = dynamic(() => import("@/components/call-to-action"), {
  ssr: false,
  loading: () => <div className="h-32 bg-gray-100 animate-pulse rounded"></div>,
});
import Head from "next/head";
import { useEffect, useState } from "react";
import { PlusIcon } from "@phosphor-icons/react";

import { motion } from "framer-motion";
import { titleAnimate, titleInitial } from "@/components/animations";
import MapBackground from "@/components/map-background";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import numeral from "numeral";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { ArrowRightIcon } from "@phosphor-icons/react";
import CurrencyInput from "react-currency-input-field";
import {
  BarcodeIcon,
  CalculatorIcon,
  FileTextIcon,
  GearIcon,
  HandshakeIcon,
  LifebuoyIcon,
  RocketIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { SlackLogoIcon } from "@phosphor-icons/react";

const formatter = new Intl.NumberFormat("en-US", {
  currency: "AUD",
  style: "currency",
  currencyDisplay: "narrowSymbol",
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
  trailingZeroDisplay: "stripIfInteger",
  roundingIncrement: 5,
});

const subscriptionTiers = [
  {
    name: "Storefront",
    price: 249,
    annualPrice: 2490,
    skuLimit: 0,
    brandLimit: 0,
    textClass: "text-stone-100",
  },
  {
    name: "Storefront + PIM (Standard)",
    price: 690,
    annualPrice: 6950,
    skuLimit: 50000,
    brandLimit: 5,
    textClass: "text-emerald-500",
  },
  {
    name: "Storefront + PIM (Pro)",
    price: 1490,
    annualPrice: 14990,
    skuLimit: 250000,
    // infinte brands
    brandLimit: Infinity,
    textClass: "text-amber-500",
  },
];

const fixedMonthlyPrices = {
  revShareCap: 5000,
  fulfilmentRevShareCap: 5000,
  revSharePercent: 0.01,
  fulfilmentRevSharePercent: 0.02,
  freeRegoLookups: 500,
  partbotApiCost: 150,
  regoLookupCost: 0.25,
};

// Force SSR to avoid framer-motion static generation issues
export async function getServerSideProps() {
  return { props: {} };
}

export default function Pricing() {
  const [annualBilling, setAnnualBilling] = useState(false);
  const [subscription, setSubscription] = useState(subscriptionTiers[0]);
  const [skuCount, setSkuCount] = useState(0);
  const [monthlySalesRevenue, setMonthlySalesRevenue] = useState(100000);
  const [monthlyFulfilmentRevenue, setMonthlyFulfilmentRevenue] =
    useState(45550);
  const [regoLookups, setRegoLookups] = useState(500);
  const [brandCount, setBrandCount] = useState(0);
  const [partbotApi, setPartbotApi] = useState(false);
  const [totalMonthlyCost, setTotalMonthlyCost] = useState(0);

  const getPriceTier = () => {
    // find the right price tier and either price or annualPrice based on subscriptionTiers.skuLimit, brandLimit; if skuCount is 0, return the first tier
    const priceTier =
      subscriptionTiers.find((tier) => {
        if (skuCount === 0 && tier.skuLimit === 0) {
          return tier;
        } else if (skuCount <= tier.skuLimit && brandCount <= tier.brandLimit) {
          return tier;
        }
      }) || subscriptionTiers[0];

    return priceTier;
  };

  const calculateMonthlyCost = () => {
    let monthlyCost = 0;

    let subscription = getPriceTier();

    setSubscription(subscription);

    const revShare = Math.min(
      monthlySalesRevenue * fixedMonthlyPrices.revSharePercent,
      fixedMonthlyPrices.revShareCap,
    );
    const fulfilmentRevShare = Math.min(
      monthlyFulfilmentRevenue * fixedMonthlyPrices.fulfilmentRevSharePercent,
      fixedMonthlyPrices.fulfilmentRevShareCap,
    );

    // if skuCount is greater than 50000, subtract 500 from regoLookups as we include 500 free lookups for the Pro tier
    const adjustedRegoLookups =
      skuCount > 50000
        ? Math.max(Number(regoLookups) - fixedMonthlyPrices.freeRegoLookups, 0)
        : Number(regoLookups) || 0;

    monthlyCost =
      (annualBilling ? subscription.annualPrice / 12 : subscription.price) +
      revShare +
      fulfilmentRevShare +
      adjustedRegoLookups * fixedMonthlyPrices.regoLookupCost +
      (partbotApi ? fixedMonthlyPrices.partbotApiCost : 0);

    return monthlyCost;
  };

  useEffect(() => {
    // calculate monthly cost
    const monthlyCost = calculateMonthlyCost();

    setTotalMonthlyCost(monthlyCost);
  }, [
    skuCount,
    brandCount,
    monthlySalesRevenue,
    monthlyFulfilmentRevenue,
    regoLookups,
    partbotApi,
    annualBilling,
  ]);

  useEffect(() => {
    const monthlyCost = calculateMonthlyCost();

    setTotalMonthlyCost(monthlyCost);
  }, []);

  const getPricingParams = () => {
    // create a url string with the pricing parameters
    const params = new URLSearchParams({
      skuCount,
      monthlySalesRevenue,
    });

    // if params include ? or & remove them
    // return params.toString().replace(/[?&]/, "");
    return params.toString();
  };

  return (
    <div>
      <Head>
        <title>Partbot - Platform Pricing</title>
      </Head>
      <Header />
      <main>
        <MapBackground />
        <div className="relative flex min-h-[70dvh] flex-col items-end justify-end pt-24">
          <div className="container mx-auto">
            <div className="relative sm:overflow-hidden">
              <div className="mt:py-16 wrap-balance relative px-6 py-6 xl:px-6">
                <motion.h1
                  initial={titleInitial}
                  whileInView={titleAnimate()}
                  viewport={{ once: true }}
                  className="text-pretty bg-linear-to-r pb-5 text-4xl font-bold leading-none drop-shadow-lg text-stone-50 lg:text-6xl xl:text-7xl"
                >
                  Platform Pricing
                </motion.h1>
                <motion.p
                  initial={titleInitial}
                  whileInView={titleAnimate(0.25)}
                  viewport={{ once: true }}
                  className="mt-3 max-w-prose text-balance text-lg text-emerald-200/75 sm:max-w-3xl md:mt-6"
                >
                  Transparent pricing. Select the features you need and use our
                  price calculator to understand your monthly costs.
                </motion.p>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto grid gap-4 md:mt-12 md:grid-cols-3 md:gap-6">
          {/* Storefront Plan */}
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dotted border-emerald-400/20 bg-stone-950/80 px-6 py-10">
            <div className="mb-4 bg-linear-to-r from-stone-300 via-white to-stone-300 bg-clip-text text-center text-sm font-bold uppercase tracking-widest text-transparent">
              {subscriptionTiers[0].name}
            </div>
            <div className="mb-2 text-center text-5xl font-bold text-emerald-50">
              {formatter.format(subscriptionTiers[0].price)}
            </div>
            <div className="mb-2 text-center text-[13px] font-medium text-stone-200">
              AUD per month
            </div>
            <div className="mb-8 text-center text-[13px] text-stone-400">
              {formatter.format(subscriptionTiers[0].annualPrice)} per year
              (save{" "}
              {formatter.format(
                subscriptionTiers[0].price * 12 -
                  subscriptionTiers[0].annualPrice,
              )}
              )
            </div>
            <ul className="mb-6 space-y-3 text-center text-sm text-stone-100">
              <li>Supplier Product Sync</li>
              <li>One-Click Fulfilment</li>
              <li>Vehicle Search Plug-ins</li>
              <li>Real-time Shipping Rates</li>
              <li>Unlimited Sales Channels</li>
            </ul>
          </div>

          {/* PIM + Storefront Standard */}
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dotted border-emerald-400/60 bg-stone-950 px-6 py-10">
            <div className="mb-4 text-center text-sm font-bold uppercase tracking-widest text-emerald-500">
              {subscriptionTiers[1].name}
            </div>
            <div className="mb-2 text-center text-5xl font-bold text-emerald-50">
              {formatter.format(subscriptionTiers[1].price)}
            </div>
            <div className="mb-2 text-center text-[13px] font-medium text-stone-200">
              AUD per month
            </div>
            <div className="mb-8 text-center text-[13px] text-stone-400">
              {formatter.format(subscriptionTiers[1].annualPrice)} per year
              (save{" "}
              {formatter.format(
                subscriptionTiers[1].price * 12 -
                  subscriptionTiers[1].annualPrice,
              )}
              )
            </div>
            <ul className="mb-6 space-y-3 text-center text-sm text-stone-100">
              <li>
                <span className="text-emerald-500">Storefront plus...</span>
              </li>
              <li>Catalogue Platform (PIM) Access</li>
              <li>Vehicle to Parts Linking</li>
              <li>50,000 SKU Limit</li>
              <li>API + ERP Integration</li>
              <li>Analytics & Customer Segmenting</li>
            </ul>
          </div>

          {/* Storefront + PIM (Pro) */}
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dotted border-amber-400/20 bg-stone-950 px-6 py-10">
            <div className="mb-4 text-center text-sm font-bold uppercase tracking-widest text-amber-500">
              {subscriptionTiers[2].name}
            </div>
            <div className="mb-2 text-center text-5xl font-bold text-amber-50">
              {formatter.format(subscriptionTiers[2].price)}
            </div>
            <div className="mb-2 text-center text-[13px] font-medium text-stone-200">
              AUD per month
            </div>
            <div className="mb-8 text-center text-[13px] text-stone-400">
              {formatter.format(subscriptionTiers[2].annualPrice)} per year
              (save{" "}
              {formatter.format(
                subscriptionTiers[2].price * 12 -
                  subscriptionTiers[2].annualPrice,
              )}
              )
            </div>
            <ul className="mb-6 space-y-3 text-center text-sm text-stone-100">
              <li>
                <span className="text-amber-500">Standard plus...</span>
              </li>
              <li>250,000 SKU Limit</li>
              <li>500 Free Rego Lookups per month</li>
              <li>Stockist Network Management</li>
              <li>BYO Vehicle Databases (e.g. TecDoc, ACES)</li>
            </ul>
          </div>
        </div>

        <div className="container mx-auto mt-4 grid gap-4 md:mt-6 md:grid-cols-3 md:gap-6">
          <div className="relative flex flex-col items-center justify-start rounded-3xl border border-dotted border-emerald-400/20 bg-stone-950/80 px-6 py-12">
            <div className="absolute left-3 top-3 flex aspect-square size-8 items-center justify-center rounded-full bg-amber-500 text-stone-950">
              <PlusIcon />
            </div>
            <div className="mb-4 text-center text-sm font-bold uppercase tracking-widest text-emerald-50">
              All Subscriptions
            </div>
            <div className="mb-4 text-center text-3xl font-bold text-emerald-500">
              1% Rev Share
            </div>
            <div className="mb-4 space-y-1 text-balance text-center text-sm text-stone-100">
              <p>Capped at $5k/mo.</p>
              <p>No minimum. Catalogued products only.</p>
            </div>
          </div>

          <div className="relative flex flex-col items-center justify-start rounded-3xl border border-dotted border-emerald-400/20 bg-stone-950/80 px-6 py-12">
            <Badge variant="secondary" className="absolute left-4 top-4">
              Add-on
            </Badge>
            <div className="mb-4 text-center text-sm font-bold uppercase tracking-widest text-emerald-50">
              Fulfilment Service
            </div>
            <div className="mb-4 text-center text-3xl font-bold text-emerald-500">
              2% Rev Share
            </div>
            <div className="mb-4 space-y-1 text-balance text-center text-sm text-stone-100">
              <p>Maximum $20 per item.</p>
              <p>Capped at $5k/mo.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 md:gap-6">
            <div className="relative flex flex-col items-center justify-start rounded-3xl border border-dotted border-emerald-400/20 bg-stone-950/80 px-8 pb-6 pt-16">
              <Badge variant="secondary" className="absolute left-4 top-4">
                Add-on
              </Badge>
              <div className="mb-4 text-center text-sm font-bold uppercase tracking-widest text-emerald-50">
                Data / API
              </div>
              <div className="mb-2 text-center text-3xl font-bold text-emerald-500">
                $150
              </div>
              <div className="mb-4 text-center text-xs font-medium text-stone-200">
                per month
              </div>
              <div className="text-balance text-center text-xs text-stone-400">
                Available as a standalone subscription
              </div>
            </div>

            <div className="relative flex flex-col items-center justify-start rounded-3xl border border-dotted border-emerald-400/20 bg-stone-950/80 px-8 pb-6 pt-16">
              <Badge variant="secondary" className="absolute left-4 top-4">
                Add-on
              </Badge>
              <div className="mb-4 text-center text-sm font-bold uppercase tracking-widest text-emerald-50">
                Rego Search
              </div>
              <div className="mb-2 text-center text-3xl font-bold text-emerald-500">
                {/* Convert to cents and append 'c' */}
                {fixedMonthlyPrices.regoLookupCost.toFixed(2) + "c"}
              </div>
              <div className="mb-4 text-center text-xs font-medium text-stone-200">
                per lookup
              </div>
              <div className="text-balance text-center text-xs text-stone-400">
                Volume discounts available
              </div>
            </div>
          </div>
        </div>

        {/* Calculator */}
        <div className="container mx-auto my-4 md:my-6">
          <div className="relative grid items-center justify-start gap-4 md:grid-cols-3 md:gap-6">
            <div className="grid w-full items-start gap-4 rounded-3xl border border-dotted border-emerald-400/20 bg-stone-950/80 p-6 md:col-span-2 md:gap-6 md:p-12">
              <div className="col-span-2 grid items-start gap-10">
                <div className="grid w-full grid-cols-1 items-start self-start">
                  <div className="mb-6 flex items-center gap-2 text-left text-sm font-bold uppercase tracking-widest text-emerald-500">
                    <CalculatorIcon className="size-5" />
                    <span>Price Calculator</span>
                  </div>
                  <div className="col-span-2 grid gap-4 md:grid-cols-3 md:gap-6">
                    <div className="grid gap-3">
                      <div className="text-sm text-stone-100">
                        Monthly Sales Revenue
                      </div>
                      <CurrencyInput
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        intlConfig={{ locale: "en-US", currency: "USD" }}
                        value={monthlySalesRevenue}
                        onValueChange={setMonthlySalesRevenue}
                      />
                    </div>
                    <div className="grid gap-3">
                      <div className="text-sm text-stone-100">
                        Monthly Fulfilment Revenue
                      </div>
                      <CurrencyInput
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        intlConfig={{ locale: "en-US", currency: "USD" }}
                        value={monthlyFulfilmentRevenue}
                        onValueChange={setMonthlyFulfilmentRevenue}
                      />
                    </div>

                    <div className="grid gap-3">
                      <div className="text-sm text-stone-100">Rego Lookups</div>
                      <CurrencyInput
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        prefix=""
                        value={regoLookups}
                        onValueChange={setRegoLookups}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid w-full grid-cols-1 items-start self-start">
                  <div className="w-full">
                    <div className="text-sm text-stone-100">
                      Catalogued Products
                    </div>
                    <div className="flex w-full items-center gap-8">
                      <Slider
                        className=" w-4/6 md:w-10/12"
                        defaultValue={[0]}
                        max={250000}
                        step={5000}
                        onValueChange={(value) => {
                          setSkuCount(value[0]);
                          // if value is 0, set brandCount to 0, otherwise, if brandCount is 0, set it to 1
                          setBrandCount(
                            value[0] === 0
                              ? 0
                              : brandCount > 1
                              ? brandCount
                              : 1,
                          );
                        }}
                      />

                      <div className="-mt-1 w-2/6 rounded-md border border-dotted border-emerald-500 py-2.5 text-center text-xl font-bold leading-none tracking-wider text-stone-100 md:w-2/12">
                        {numeral(skuCount).format("0a")}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1">
                  <div className="col-span-1 flex flex-col flex-wrap gap-4 md:flex-row md:items-end md:gap-12">
                    <div className="mb-4 grid w-28 gap-3 md:mb-0">
                      <div className="text-sm text-stone-100">Brands</div>
                      <Input
                        type="number"
                        step="1"
                        value={brandCount}
                        onChange={(e) => setBrandCount(e.target.value)}
                      />
                    </div>

                    <div className="flex items-center space-x-4 pb-2">
                      <Switch
                        id="api-access"
                        value={partbotApi}
                        onCheckedChange={setPartbotApi}
                      />
                      <label
                        htmlFor="api-access"
                        className="text-sm text-stone-100"
                      >
                        Partbot API Access
                      </label>
                    </div>

                    <div className="flex items-center space-x-4 pb-2">
                      <Switch
                        id="annual-subscription"
                        value={annualBilling}
                        onCheckedChange={setAnnualBilling}
                      />
                      <label
                        htmlFor="annual-subscription"
                        className="text-sm text-stone-100"
                      >
                        Annual Subscription
                      </label>
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <div className="inline-flex flex-col items-start gap-2 text-pretty rounded-lg bg-stone-900/70 px-4 py-3 pr-6 text-xs text-stone-500 md:leading-none">
                    <p>
                      All prices are in Australian Dollars (AUD) and include
                      GST.
                    </p>
                    <p>
                      <sup>*</sup>Estimated Monthly Cost is not a binding quote
                      as it does not consider the maximum fulfillment item cap.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Totals */}
            <div className="flex h-full flex-col justify-start rounded-2xl border border-dotted border-emerald-500/50 bg-stone-950/80 p-6 md:col-span-1 md:p-12">
              <div className="mb-6 flex items-center gap-2 text-left text-sm font-bold uppercase tracking-widest text-emerald-500">
                <FileTextIcon className="size-5" />
                <span>Cost Breakdown</span>
              </div>
              <div className="mb-12 grid w-full grid-cols-3 gap-y-3 text-xs text-stone-400">
                <div className="col-span-2 flex gap-1 whitespace-nowrap text-left">
                  <span className="sr-only">Subscription</span>
                  <span className="text-xs">
                    <span className={subscription.textClass}>
                      {subscription.name}
                    </span>
                  </span>
                </div>
                <div className="text-right">
                  <span>
                    {formatter.format(
                      annualBilling
                        ? subscription.annualPrice
                        : subscription.price,
                    )}
                  </span>
                  <span className="text-xs">
                    {annualBilling ? "/yr" : "/mo"}
                  </span>
                </div>

                {/* if annualBilling is true, show Annual Subscription Discount row */}
                {annualBilling && (
                  <>
                    <div className="col-span-2 flex gap-1 whitespace-nowrap text-left">
                      <span>Annual Discount</span>
                    </div>
                    <div className="text-right">
                      -
                      {formatter.format(
                        subscription.price * 12 - subscription.annualPrice,
                      )}
                    </div>
                  </>
                )}

                <div className="col-span-2 text-left">
                  <span>1% Rev Share</span>
                  {/* is capped to 5000? */}
                  <span className="text-xs text-stone-300">
                    {Math.min(
                      monthlySalesRevenue * fixedMonthlyPrices.revSharePercent,
                      fixedMonthlyPrices.revShareCap,
                    ) === 5000 && " (capped)"}
                  </span>
                </div>
                <div className="text-right">
                  {formatter.format(
                    Math.min(
                      monthlySalesRevenue * fixedMonthlyPrices.revSharePercent,
                      fixedMonthlyPrices.revShareCap,
                    ),
                  )}
                </div>

                <div className="col-span-2 text-left">
                  <span>2% Fulfilment Rev Share</span>
                  {/* is capped to 5000? */}
                  <span className="text-xs text-stone-300">
                    {Math.min(
                      monthlyFulfilmentRevenue *
                        fixedMonthlyPrices.fulfilmentRevSharePercent,
                      fixedMonthlyPrices.fulfilmentRevShareCap,
                    ) === fixedMonthlyPrices.fulfilmentRevShareCap &&
                      " (capped)"}
                  </span>
                </div>
                <div className="text-right">
                  {formatter.format(
                    Math.min(
                      monthlyFulfilmentRevenue *
                        fixedMonthlyPrices.fulfilmentRevSharePercent,
                      fixedMonthlyPrices.fulfilmentRevShareCap,
                    ),
                  )}
                </div>

                <div className="col-span-2 text-left">Partbot API</div>
                <div className="text-right">
                  {formatter.format(
                    partbotApi ? fixedMonthlyPrices.partbotApiCost : 0,
                  )}
                </div>

                <div className="col-span-2 text-left">
                  <span>Rego Lookups</span>
                  <span className="text-xs text-stone-300">
                    {skuCount > 50000 ? " (500 free)" : ""}
                  </span>
                </div>
                <div className="text-right">
                  {formatter.format(
                    Math.max(
                      (skuCount > 50000
                        ? regoLookups - fixedMonthlyPrices.freeRegoLookups
                        : regoLookups) * fixedMonthlyPrices.regoLookupCost,
                      0,
                    ) || 0,
                  )}
                </div>
              </div>
              <div className="flex h-full w-full flex-col items-center justify-end">
                <div className="text-sm font-medium tracking-wider text-stone-100">
                  Estimated Monthly Cost<sup>*</sup>
                </div>
                <div className="mt-2 text-5xl font-bold text-emerald-500">
                  {formatter.format(totalMonthlyCost)}
                </div>
                <div className="mt-12 w-full">
                  <Button
                    size="lg"
                    className="w-full bg-emerald-500"
                    onClick={() => {
                      window.location.href = `/contact?${getPricingParams()}`;
                    }}
                  >
                    <span className="mr-2">Get Started</span>
                    <ArrowRightIcon />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="relative grid grid-cols-1 items-center justify-start gap-4  rounded-3xl border border-dotted border-emerald-400/20 bg-stone-950/80 p-6 pb-8 md:gap-6 md:p-12">
              <div className="grid gap-3 md:grid-cols-5 md:items-start md:gap-12">
                <div className="col-span-1 md:col-span-2">
                  <div className="mb-6 flex items-center gap-3 text-left text-sm font-bold uppercase tracking-widest text-emerald-500">
                    <RocketIcon className="size-8" />
                    <span>Enterprise Pricing</span>
                  </div>
                  <p className="mb-6 text-balance font-medium text-stone-100">
                    Need more support and compliance features or pricing
                    doesn&apos;t work for your company?
                  </p>
                  <Button size="xs">
                    <Link href="/contact">Request a Quote</Link>
                  </Button>
                </div>
                <div className="mt-6 md:mt-16">
                  <ul className="space-y-3 text-left text-sm text-emerald-500">
                    <li className="flex gap-3">
                      <BarcodeIcon className="size-5" />
                      <span>250k+ SKU Limit</span>
                    </li>
                    <li className="flex gap-3">
                      <UsersIcon className="size-5" />
                      <span>SSO / Multi-Tenant</span>
                    </li>
                  </ul>
                </div>
                <div className="md:mt-16">
                  <ul className="space-y-3 text-left text-sm text-emerald-500">
                    <li className="flex gap-3">
                      <GearIcon className="size-5" />
                      <span>Custom API Integrations</span>
                    </li>
                    <li className="flex gap-3">
                      <HandshakeIcon className="size-5" />
                      <span className="text-balance">
                        Onboarding & Migration Support
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="md:mt-16">
                  <ul className="space-y-3 text-left text-sm text-emerald-500">
                    <li className="flex gap-3">
                      <LifebuoyIcon className="size-5" />
                      <span>Support & Uptime SLA</span>
                    </li>
                    <li className="flex gap-3">
                      <SlackLogoIcon className="size-5" />
                      <span>Dedicated Slack Support</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <CallToAction />
      <Footer />
    </div>
  );
}
